// src/viewmodels/useBookmarkViewModel.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookmarkStore } from '../stores/bookmark.store.ts';
import { BookmarkRepository } from '../repositories/bookamrk.repository.ts';
import { userStore } from '../stores/user.store.ts';
import { useCurrencyStore } from '../stores/currency.store.ts';

// BookmarkScreen용
export function useBookmarkListViewModel() {
  const user = userStore((s) => s.user);
  const isLoggedIn = !!user;

  const bookmarkList = useBookmarkStore((s) => s.bookmarkList);
  const pagination = useBookmarkStore((s) => s.pagination);
  const loading = useBookmarkStore((s) => s.listLoading);
  const error = useBookmarkStore((s) => s.listError);

  const [page, setPage] = useState(1);
  const currency = useCurrencyStore((s) => s.currency);
  const hasMore = pagination ? pagination.current_page < pagination.total_page : false;

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // 금액은 서버가 통화별로 환산해 내려준다 — 통화가 바뀌면 다시 받아야 한다.
  // 안 그러면 PriceDisplay 가 기호만 새 통화로 바꿔 그려 «숫자는 그대로,
  // 기호만 바뀐» 화면이 된다.
  useEffect(() => {
    if (!isLoggedIn) return;
    BookmarkRepository.fetchBookmarkList({ page: 1, force: true });
    setPage(1);
  }, [isLoggedIn, currency]);

  // 무한 스크롤
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          BookmarkRepository.fetchBookmarkList({ page: nextPage });
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, page]);

  return {
    isLoggedIn,
    bookmarkList,
    pagination,
    loading,
    error,
    hasMore,
    loadMoreRef,
  };
}

// 서비스 카드/상세에서 사용하는 toggle 훅
export function useBookmarkToggle(uuid: string | undefined) {
  const navigate = useNavigate();
  const user = userStore((s) => s.user);
  const isLoggedIn = !!user;

  const isBookmarked = useBookmarkStore((s) => (uuid ? s.isBookmarked(uuid) : false));
  const isToggling = useBookmarkStore((s) => (uuid ? s.togglingUuids.has(uuid) : false));

  const toggle = useCallback(() => {
    if (!isLoggedIn) {
      navigate('/sign-in');
      return;
    }
    if (!uuid || isToggling) return;
    BookmarkRepository.toggleBookmark(uuid);
  }, [isLoggedIn, uuid, isToggling, navigate]);

  return { isBookmarked, isToggling, toggle };
}
